import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
} from "@mui/material";
import { Bar, Pie } from "react-chartjs-2";
import Layout from "../components/layout/GSTlayout";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const GSTR2Portal = () => {
  // Sample inward supply data
  const [inwardSupplies, setInwardSupplies] = useState([
    {
      gstin: "07AATCA2146C1Z4",
      supplierName: "ABC Enterprises",
      invoiceNo: "INV-2024-001",
      invoiceDate: "15-01-2024",
      taxableValue: 50000,
      igst: 9000,
      cgst: 4500,
      sgst: 4500,
    },
    {
      gstin: "09BBTCA3257D2Y5",
      supplierName: "XYZ Industries",
      invoiceNo: "INV-2024-002",
      invoiceDate: "20-01-2024",
      taxableValue: 75000,
      igst: 13500,
      cgst: 6750,
      sgst: 6750,
    },
  ]);

  // Bar chart data for tax analysis
  const taxAnalysisData = {
    labels: ["IGST", "CGST", "SGST"],
    datasets: [
      {
        label: "Tax Amounts (₹)",
        data: [
          inwardSupplies.reduce((sum, supply) => sum + supply.igst, 0),
          inwardSupplies.reduce((sum, supply) => sum + supply.cgst, 0),
          inwardSupplies.reduce((sum, supply) => sum + supply.sgst, 0),
        ],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
        ],
      },
    ],
  };

  // Pie chart data for supplier-wise distribution
  const supplierDistributionData = {
    labels: inwardSupplies.map((supply) => supply.supplierName),
    datasets: [
      {
        label: "Supplier-wise Taxable Value",
        data: inwardSupplies.map((supply) => supply.taxableValue),
        backgroundColor: ["rgba(255, 99, 132, 0.6)", "rgba(54, 162, 235, 0.6)"],
      },
    ],
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>
          GSTR-2 Inward Supplies
        </Typography>

        <Grid container spacing={3}>
          {/* Inward Supplies Table */}
          <Grid item xs={12}>
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>GSTIN</TableCell>
                      <TableCell>Supplier Name</TableCell>
                      <TableCell>Invoice No</TableCell>
                      <TableCell>Invoice Date</TableCell>
                      <TableCell align="right">Taxable Value</TableCell>
                      <TableCell align="right">IGST</TableCell>
                      <TableCell align="right">CGST</TableCell>
                      <TableCell align="right">SGST</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inwardSupplies.map((supply, index) => (
                      <TableRow key={index}>
                        <TableCell>{supply.gstin}</TableCell>
                        <TableCell>{supply.supplierName}</TableCell>
                        <TableCell>{supply.invoiceNo}</TableCell>
                        <TableCell>{supply.invoiceDate}</TableCell>
                        <TableCell align="right">
                          ₹{supply.taxableValue.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          ₹{supply.igst.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          ₹{supply.cgst.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          ₹{supply.sgst.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Tax Analysis Charts */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tax Distribution
              </Typography>
              <Bar
                data={taxAnalysisData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                    title: {
                      display: true,
                      text: "Tax Distribution Across Categories",
                    },
                  },
                }}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Supplier-wise Taxable Value
              </Typography>
              <Pie
                data={supplierDistributionData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                    title: {
                      display: true,
                      text: "Supplier Distribution",
                    },
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between">
              <Button variant="contained" color="primary">
                Verify Inward Supplies
              </Button>
              <Button variant="contained" color="secondary">
                File GSTR-2
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default GSTR2Portal;
